import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import OrdersList from './pages/OrdersList';
import CompanyReport from './pages/CompanyReport';
import ProductsList from './pages/ProductsList';
import Statistics from './pages/Statistics';
import UserManagement from './pages/UserManagement';
import Login from './pages/Login';
import Register from './pages/Register';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import dataService from './services/dataService';
import './index.css';

// Componente para determinar a view atual baseada na URL
const AppContent = () => {
  const location = useLocation();
  const [orderFilters, setOrderFilters] = useState({});

  // Determinar a view atual baseada na URL
  const getCurrentView = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') return 'dashboard';
    if (path === '/orders') return 'orders';
    if (path === '/company-report') return 'company-report';
    if (path === '/products') return 'products';
    if (path === '/statistics') return 'statistics';
    if (path === '/users') return 'users';
    return 'dashboard';
  };

  // Função para navegar para lista de ordens com filtros específicos (drill-down)
  const handleNavigateToOrders = (filters = {}) => {
    setOrderFilters(filters);
    // Redirecionar para a página de ordens
    window.location.href = '/orders';
  };

  return (
    <Layout currentView={getCurrentView()} onRefresh={() => window.location.reload()}>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard onNavigateToOrders={handleNavigateToOrders} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <OrdersList initialFilters={orderFilters} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company-report"
          element={
            <ProtectedRoute>
              <CompanyReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute>
              <ProductsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/statistics"
          element={
            <ProtectedRoute>
              <Statistics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requireAdmin={true}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/import"
          element={<Navigate to="/dashboard" replace />}
        />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await dataService.loadData();
      console.log('Dados carregados com sucesso');
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar dados. Verifique se o arquivo JSON está disponível.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderLoadingState = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h3 className="text-lg font-semibold text-dark-text mb-2">Carregando Dashboard</h3>
        <p className="text-dark-text-secondary">
          Processando dados das ordens de serviço...
        </p>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="card-dark p-8 max-w-md text-center">
        <div className="w-16 h-16 bg-danger rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-dark-text mb-2">Erro ao Carregar</h3>
        <p className="text-dark-text-secondary mb-4">{error}</p>
        <button
          onClick={loadInitialData}
          className="btn-primary"
        >
          Tentar Novamente
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Router>
        <Layout currentView="dashboard" onRefresh={loadInitialData} isLoading={isLoading}>
          {renderLoadingState()}
        </Layout>
      </Router>
    );
  }

  if (error) {
    return (
      <Router>
        <Layout currentView="dashboard" onRefresh={loadInitialData} isLoading={isLoading}>
          {renderErrorState()}
        </Layout>
      </Router>
    );
  }

  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
